// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useMemo, useState, useCallback, Fragment } from 'react'
import { formatUsDate } from '../../api/format'
import { statusColor } from '../../api/status'
import { Plus, Loader2, Building2, Receipt, ArrowLeft, Download } from 'lucide-react'
import * as api from '../../api/client'
import SearchableSelect from '../../components/common/SearchableSelect'
import { friendlyError } from '../../api/errors'
import { previousMonthPeriod } from './billingPeriod'
import { usePaged, Pager, DEFAULT_PAGE_SIZE } from '../../components/Pager'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'
import CareRxNursesTeams from './CareRxNursesTeams'
import PatientLanguageBadge from '../../components/ui/PatientLanguageBadge'
import DateInput from '../../components/ui/DateInput'
import { SelectField } from '../../components/ui/FormFields'
import { LOA_NEXT, setCareRxLoaStatus } from '../../api/carerxBilling'
import { EditVisitModal } from '../pot/billing/VisitsPanel'
import type { PotAgency, PotVisitRow } from '../pot/potApi'

// D-UI-2: funnel columns, keyed by the header label each table already maps over.
const titleCase = (s: string) => s.replace(/\b\w/g, c => c.toUpperCase())
const credStatusText = (c: any) =>
  c.expired ? 'EXPIRED — unassignable' : c.days_left <= 60 ? `expires in ${c.days_left}d` : 'current'
const INVOICE_FUNNELS: FunnelColumn<any>[] = [
  { key: 'Invoice #', label: 'Invoice #', value: i => i.invoice_number },
  { key: 'Period', label: 'Period', value: i => `${i.period_start} → ${i.period_end}` },
  { key: 'Visits', label: 'Visits', value: i => i.visit_count ?? '—' },
  { key: 'Total', label: 'Total', value: i => i.total },
  { key: 'Status', label: 'Status', value: i => titleCase(String(i.status).replace('_', ' ')) },
]
const LOA_FUNNELS: FunnelColumn<any>[] = [
  { key: 'LOA #', label: 'LOA #', value: l => l.loa_number },
  { key: 'Patient', label: 'Patient', value: l => l.patient_initials || '—' },
  { key: 'Period', label: 'Period', value: l => `${l.start_date || '—'} → ${l.end_date || '—'}` },
  { key: 'Status', label: 'Status', value: l => l.status },
]
const CRED_FUNNELS: FunnelColumn<any>[] = [
  { key: 'nurse', label: 'Nurse', value: () => 'on file' },
  { key: 'type', label: 'Type', value: c => c.credential_type },
  { key: 'number', label: 'Number', value: c => c.credential_number || '—' },
  { key: 'expires', label: 'Expires', value: c => c.expires_at },
  { key: 'status', label: 'Status', value: credStatusText },
]

// Fetch a stored CareRx document (cookie-auth) and open it in a tab via a blob URL. Pass a
// pre-opened window (claimed inside the click gesture) so popup blockers can't eat it.
async function openDocument(documentId: string, target?: Window | null) {
  const res = await fetch(`/api/carerx/documents/${documentId}`, { credentials: 'include' })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  if (target) target.location.href = url
  else window.open(url, '_blank')
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

export default function CareRxBilling() {
  const [agencies, setAgencies] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const agenciesPager = usePaged(agencies, DEFAULT_PAGE_SIZE)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try { const d = await api.listCareRxAgencies(); setAgencies(d.items) }
    catch (e: any) { setError(friendlyError(e)) } finally { setLoading(false) }
  }, [])
  useEffect(() => { load() }, [load])

  if (openId) return <AgencyDetail id={openId} name={agencies.find(a => a.id === openId)?.name || 'Agency'} agencies={agencies} onBack={() => setOpenId(null)} />
  if (loading) return <Centered><Loader2 className="spin" size={20} /> Loading…</Centered>
  if (error) return <Centered>{error}</Centered>

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: 'var(--color-text-mute)' }}>{agencies.length} nursing {agencies.length === 1 ? 'agency' : 'agencies'}</span>
        <button style={primary} onClick={() => setCreating(true)}><Plus size={14} /> Add agency</button>
      </div>
      {creating && <CreateAgency onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load() }} />}
      {agencies.length === 0 ? <Centered>No nursing agencies yet.</Centered> : (
        <>
        <Pager page={agenciesPager.page} totalPages={agenciesPager.totalPages} total={agenciesPager.total} pageSize={agenciesPager.pageSize} onPage={agenciesPager.setPage} onPageSize={agenciesPager.setPageSize} label="agencies" />
        <div style={{ display: 'grid', gap: 10 }}>
          {agenciesPager.pageRows.map(a => (
            <button key={a.id} onClick={() => setOpenId(a.id)} style={card}>
              <Building2 size={18} style={{ color: 'var(--color-primary)' }} />
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{a.name}</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>{[a.city, a.state_code].filter(Boolean).join(', ') || '—'}{a.contact_name ? ` · ${a.contact_name}` : ''}</div>
              </div>
              <span style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>{a.status}</span>
            </button>
          ))}
        </div>
        <Pager page={agenciesPager.page} totalPages={agenciesPager.totalPages} total={agenciesPager.total} pageSize={agenciesPager.pageSize} onPage={agenciesPager.setPage} showPageSize={false} label="agencies" />
        </>
      )}
    </div>
  )
}

function CreateAgency({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState(''); const [city, setCity] = useState(''); const [contact, setContact] = useState('')
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null)
  const submit = async () => {
    if (!name.trim()) { setErr('Name required'); return }
    setBusy(true); setErr(null)
    try { await api.createCareRxAgency({ name: name.trim(), city: city || undefined, contact_name: contact || undefined }); onCreated() }
    catch (e: any) { setErr(friendlyError(e)); setBusy(false) }
  }
  return (
    <div style={panel}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
        <L label="Agency name *"><input style={input} value={name} onChange={e => setName(e.target.value)} /></L>
        <L label="City"><input style={input} value={city} onChange={e => setCity(e.target.value)} /></L>
        <L label="Contact"><input style={input} value={contact} onChange={e => setContact(e.target.value)} /></L>
      </div>
      {err && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>{err}</div>}
      <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
        <button style={primary} disabled={busy} onClick={submit}>{busy ? <Loader2 className="spin" size={14} /> : <Plus size={14} />} Create</button>
        <button style={ghost} onClick={onClose}>Cancel</button>
      </div>
    </div>
  )
}

function AgencyDetail({ id, name, agencies, onBack }: { id: string; name: string; agencies: PotAgency[]; onBack: () => void }) {
  const [tab, setTab] = useState<'visits' | 'nurses' | 'invoices' | 'rates' | 'loas' | 'credentials'>('visits')
  const [visits, setVisits] = useState<any[]>([])
  const [invoices, setInvoices] = useState<any[]>([])
  const [rates, setRates] = useState<any[]>([])
  const [loas, setLoas] = useState<any[]>([])
  const [creds, setCreds] = useState<any[]>([])
  const [credForm, setCredForm] = useState(false)
  const [credB, setCredB] = useState({ nurse_id: '', credential_type: 'rn_license', credential_number: '', expires_at: '' })
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [rateForm, setRateForm] = useState(false)
  const [docBusy, setDocBusy] = useState<string | null>(null)
  const [invRev, setInvRev] = useState<string | null>(null)
  const [invNotes, setInvNotes] = useState('')
  const [editing, setEditing] = useState<PotVisitRow | null>(null)
  // asp-pot's Generate Invoice took any period; last calendar month is only the default
  const [period, setPeriod] = useState(() => previousMonthPeriod())
  const invFx = useTableFunnels(invoices, INVOICE_FUNNELS)
  const loaFx = useTableFunnels(loas, LOA_FUNNELS)
  const credFx = useTableFunnels(creds, CRED_FUNNELS)
  const invoicesPager = usePaged(invFx.rows, DEFAULT_PAGE_SIZE, invFx.sig)
  const loasPager = usePaged(loaFx.rows, DEFAULT_PAGE_SIZE, loaFx.sig)
  const credsPager = usePaged(credFx.rows, DEFAULT_PAGE_SIZE, credFx.sig)

  // Owner review 2026-08-13 (Billing deep-dive): the server's invoice lifecycle is
  // draft → submitted → pending_approval → approved → paid (+ reject/resubmit), but the UI
  // offered Approve/Request-changes/Reject — one 409'd on a draft, one wasn't even a valid
  // action, and there was NO path to submitted/paid. An invoice could never leave draft
  // from the browser. Status-aware actions now; AC-7 self-approval 403s surface verbatim.
  const reviewInvoice = async (invoiceId: string, action: 'submit' | 'start_review' | 'approve' | 'reject' | 'mark_paid' | 'void') => {
    setBusy(true); setMsg(null)
    try {
      const r = await api.reviewCareRxInvoice(invoiceId, { action, notes: invNotes.trim() || undefined })
      setInvNotes(''); setInvRev(null)
      setMsg(action === 'void' ? `Invoice voided — ${r.visits_released ?? 0} visit(s) released to be invoiced again.` : 'Invoice review recorded.')
      await load()
    } catch (e: any) { setMsg(friendlyError(e)) } finally { setBusy(false) }
  }

  // asp-pot's LOA lifecycle — "signed" is what makes the rate card invoiceable (D-CRX-31 S6).
  const changeLoaStatus = async (loaId: string, status: string) => {
    setBusy(true); setMsg(null)
    try { await setCareRxLoaStatus(loaId, status); setMsg(`LOA is now ${status}.`); await load() }
    catch (e: any) { setMsg(friendlyError(e)) } finally { setBusy(false) }
  }

  const renderDoc = async (kind: 'invoice' | 'loa', recordId: string) => {
    // Owner tester pass 2026-08-14: claim the tab SYNCHRONOUSLY in the click gesture —
    // window.open after an await is eaten by popup blockers (headed Chromium proved it).
    const w = window.open('', '_blank')
    setDocBusy(recordId); setMsg(null)
    try {
      const r = kind === 'invoice' ? await api.renderCareRxInvoice(recordId) : await api.renderCareRxLoa(recordId)
      if (r.stored) await openDocument(r.document_id, w)
      else { w?.close(); setMsg('Document generated, but blob storage is not configured in this environment.') }
    } catch (e: any) { w?.close(); setMsg(friendlyError(e)) } finally { setDocBusy(null) }
  }

  const load = useCallback(async () => {
    try {
      const [v, i, r, l] = await Promise.all([
        api.listCareRxVisits(id), api.listCareRxInvoices(id), api.listCareRxRateCards(id), api.listCareRxLoas(id),
      ])
      setVisits(v.items); setInvoices(i.items); setRates(r.items); setLoas(l.items)
      // credentials store may predate mig 251 on a tenant: a 409 migration_pending is expected, not an error
      try { const c = await api.listCareRxCredentials(id); setCreds(c.credentials) } catch { setCreds([]) }
    } catch (e: any) { setMsg(friendlyError(e)) }
  }, [id])
  useEffect(() => { load() }, [load])

  const genInvoice = async () => {
    if (!period.period_start || !period.period_end || period.period_end < period.period_start) { setMsg('Pick a period: a start date on or before the end date.'); return }
    setBusy(true); setMsg(null)
    try {
      const r = await api.generateCareRxInvoice(id, period)
      setMsg(`Invoice ${r.invoice_number}: $${r.total} (${r.visit_count} visits).`); await load(); setTab('invoices')
    } catch (e: any) {
      setMsg(e?.body?.error === 'no_unbilled_visits' ? `No unbilled visits between ${formatUsDate(period.period_start)} and ${formatUsDate(period.period_end)}.`
        : e?.body?.error === 'no_rate_card' ? 'Add a rate card first.' : friendlyError(e))
    } finally { setBusy(false) }
  }

  return (
    <div>
      <button style={ghost} onClick={onBack}><ArrowLeft size={14} /> All agencies</button>
      <h2 style={{ fontSize: 18, margin: '12px 0 4px' }}>{name}</h2>
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--color-border)', margin: '12px 0' }}>
        {/* The tab key is not a label: 'loas' rendered through textTransform:capitalize reads
            "Loas". LOA is an initialism (Letter of Agreement) and stays upper-case. */}
        {(['visits', 'nurses', 'invoices', 'rates', 'loas', 'credentials'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13,
            fontWeight: tab === t ? 700 : 500, color: tab === t ? 'var(--color-primary)' : 'var(--color-text-soft)',
            borderBottom: tab === t ? '2px solid var(--color-primary)' : '2px solid transparent', marginBottom: -1, textTransform: 'capitalize' }}>{t === 'loas' ? 'LOAs' : t}</button>
        ))}
      </div>
      {msg && <div style={{ fontSize: 13, color: 'var(--color-text-soft)', background: 'var(--color-surface-alt)', padding: '8px 12px', borderRadius: 8, marginBottom: 12 }}>{msg}</div>}

      {tab === 'visits' && (
        <>
        <Table head={['Date', 'Patient', 'Therapy', 'Type', 'Hours', 'Mileage', 'City', 'Premiums', 'Billed', '']} rows={visits.map(v => [
          v.visit_date, { value: v.patient_name || '—', node: <>{v.patient_name || '—'}<PatientLanguageBadge patientId={v.patient_id} /></> }, v.therapy_drug || '—', v.visit_type, v.duration_hrs, v.mileage,
          v.city ? `${v.city}${v.state_code ? ', ' + v.state_code : ''}` : '—',
          [v.holiday && 'Holiday', v.weekend && 'Weekend', v.after_hours && 'After-hrs'].filter(Boolean).join(', ') || '—', v.billed ? 'Yes' : 'No',
          { value: '', node: v.billed ? null : <button style={linkBtn} onClick={() => setEditing(v)}>Edit</button> },
        ])} empty="No visits recorded." />
        {/* asp-pot "Edit Visit": agency, type, location, mileage and premiums, until the visit is invoiced */}
        <EditVisitModal visit={editing} agencies={agencies.filter(a => a.status === 'active')} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load() }} />
        </>
      )}
      {/* D-CRX-47 POT-7: this agency manages its own nurses & teams. */}
      {tab === 'nurses' && <CareRxNursesTeams agencyId={id} />}
      {tab === 'invoices' && (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
            <DateInput label="Period start" value={period.period_start} onChange={v => setPeriod(p => ({ ...p, period_start: v ?? '' }))} style={input} />
            <DateInput label="Period end" value={period.period_end} onChange={v => setPeriod(p => ({ ...p, period_end: v ?? '' }))} style={input} />
            <button style={primary} disabled={busy} onClick={genInvoice}>
              {busy ? <Loader2 className="spin" size={14} /> : <Receipt size={14} />} Generate invoice for {formatUsDate(period.period_start)} – {formatUsDate(period.period_end)}
            </button>
          </div>
          {invoices.length === 0 ? <Centered>No invoices yet.</Centered> : (
            <><Pager page={invoicesPager.page} totalPages={invoicesPager.totalPages} total={invoicesPager.total} pageSize={invoicesPager.pageSize} onPage={invoicesPager.setPage} onPageSize={invoicesPager.setPageSize} label="invoices" />
              {invFx.chips}
              <div style={{ overflowX: 'auto' }}><table {...invFx.tableProps} className="rwd-stack" style={{ width: '100%', minWidth: 'max-content', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>{['Invoice #', 'Period', 'Visits', 'Total', 'Status', ''].map(h => <th key={h} style={th}>{h} {invFx.funnel(h)}</th>)}</tr></thead>
              <tbody>
                {invoicesPager.pageRows.map(i => {
                  const sc = i.status === 'approved' || i.status === 'paid' ? ['#15803d', 'rgba(22,163,74,.12)']
                    : i.status === 'rejected' ? ['#b91c1c', 'rgba(220,38,38,.10)']
                    : i.status === 'pending_approval' ? ['#b45309', 'rgba(217,119,6,.12)']
                    : i.status === 'submitted' ? ['#185FA5', 'rgba(24,95,165,.10)']
                    : ['var(--color-text-mute)', 'var(--color-bg)']
                  return (
                  <Fragment key={i.id}>
                  <tr style={{ borderBottom: invRev === i.id ? 'none' : '1px solid var(--color-border)' }}>
                    <td data-label="Invoice #" style={td}>{i.invoice_number}</td>
                    <td data-label="Period" style={td}>{i.period_start} → {i.period_end}</td>
                    <td data-label="Visits" style={td}>{i.visit_count ?? '—'}</td>
                    <td data-label="Total" style={td}>${i.total}</td>
                    <td data-label="Status" style={td}>
                      <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'capitalize', padding: '3px 9px', borderRadius: 999, color: sc[0], background: sc[1] }}>{String(i.status).replace('_', ' ')}</span>
                      {i.approved_at && <span style={{ fontSize: 11, color: 'var(--color-text-mute)', marginLeft: 6 }}>{formatUsDate(i.approved_at as string)}</span>}
                    </td>
                    <td data-label="" style={{ ...td, display: 'flex', gap: 10, alignItems: 'center' }}>
                      <button style={linkBtn} onClick={() => { setInvRev(invRev === i.id ? null : i.id); setInvNotes('') }}>Review</button>
                      <button style={linkBtn} disabled={docBusy === i.id} onClick={() => renderDoc('invoice', i.id)}>
                        {docBusy === i.id ? <Loader2 className="spin" size={13} /> : <Download size={13} />} PDF
                      </button>
                    </td>
                  </tr>
                  {invRev === i.id && (
                    <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td colSpan={6} style={{ padding: '4px 8px 12px' }}>
                        <textarea style={{ width: '100%', maxWidth: 520, padding: '7px 9px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', background: 'var(--color-surface)', color: 'var(--color-text)', boxSizing: 'border-box', minHeight: 40 }}
                          placeholder="Review notes (optional)" value={invNotes} onChange={e => setInvNotes(e.target.value)} />
                        <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                          {i.status === 'draft' && <button disabled={busy} style={revBtn('#185FA5')} onClick={() => reviewInvoice(i.id, 'submit')}>Submit for review</button>}
                          {i.status === 'submitted' && <button disabled={busy} style={revBtn('#185FA5')} onClick={() => reviewInvoice(i.id, 'start_review')}>Start review</button>}
                          {i.status === 'pending_approval' && <>
                            <button disabled={busy} style={revBtn('#16a34a')} onClick={() => reviewInvoice(i.id, 'approve')}
                              title="AC-7: the nurse whose visits this invoice bills cannot approve it — the server refuses (separation of duties)">Approve</button>
                            <button disabled={busy || !invNotes.trim()} style={{ ...revBtn('#dc2626'), opacity: invNotes.trim() ? 1 : .5 }}
                              title={invNotes.trim() ? undefined : 'A rejection reason is required — write it in the notes above'}
                              onClick={() => reviewInvoice(i.id, 'reject')}>Reject</button>
                          </>}
                          {i.status === 'approved' && <button disabled={busy} style={revBtn('#16a34a')} onClick={() => reviewInvoice(i.id, 'mark_paid')}>Mark paid</button>}
                          {i.status === 'rejected' && <button disabled={busy} style={revBtn('#185FA5')} onClick={() => reviewInvoice(i.id, 'submit')}>Resubmit</button>}
                          {i.status === 'paid' && <span style={{ fontSize: 12, color: '#15803d', fontWeight: 700 }}>Paid — lifecycle complete</span>}
                          {i.status === 'voided' && <span style={{ fontSize: 12, color: '#b91c1c', fontWeight: 700 }}>Voided — its visits were released</span>}
                          {i.status !== 'paid' && i.status !== 'voided' && (
                            <button disabled={busy || !invNotes.trim()} style={{ ...revBtn('#dc2626'), opacity: invNotes.trim() ? 1 : .5 }}
                              title={invNotes.trim() ? 'Cancel this invoice and release its visits to be invoiced again' : 'A void reason is required — write it in the notes above'}
                              onClick={() => reviewInvoice(i.id, 'void')}>Void</button>
                          )}
                          <span style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>draft → submitted → pending approval → approved → paid</span>
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                )})}
              </tbody>
            </table></div>
              <Pager page={invoicesPager.page} totalPages={invoicesPager.totalPages} total={invoicesPager.total} pageSize={invoicesPager.pageSize} onPage={invoicesPager.setPage} showPageSize={false} label="invoices" /></>
          )}
        </>
      )}
      {tab === 'rates' && (
        <>
          <button style={{ ...primary, marginBottom: 12 }} onClick={() => setRateForm(v => !v)}>
            <Plus size={14} /> {rateForm ? 'Close' : 'New rate card'}
          </button>
          {rateForm && <RateCardForm agencyId={id} onSaved={() => { setRateForm(false); load() }} />}
          <Table head={['Effective from', 'Mileage/mi', 'Weekend %', 'Holiday %', 'Admin fee']} rows={rates.map(r => [
            r.effective_from, `$${r.mileage_per_mile}`, `${r.weekend_pct}%`, `${r.holiday_pct}%`, `$${r.admin_fee}`,
          ])} empty="No rate cards yet — add one to enable invoicing." />
        </>
      )}
      {tab === 'loas' && (
        loas.length === 0 ? <Centered>No Letters of Agreement yet.</Centered> : (
          <><Pager page={loasPager.page} totalPages={loasPager.totalPages} total={loasPager.total} pageSize={loasPager.pageSize} onPage={loasPager.setPage} onPageSize={loasPager.setPageSize} label="LOAs" />
          {loaFx.chips}
          <div style={{ overflowX: 'auto' }}><table {...loaFx.tableProps} className="rwd-stack" style={{ width: '100%', minWidth: 'max-content', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>{['LOA #', 'Patient', 'Period', 'Status', ''].map(h => <th key={h} style={th}>{h} {loaFx.funnel(h)}</th>)}</tr></thead>
            <tbody>
              {loasPager.pageRows.map(l => (
                <tr key={l.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td data-label="LOA #" style={td}>{l.loa_number}</td>
                  <td data-label="Patient" style={td}>{l.patient_initials || '—'}</td>
                  <td data-label="Period" style={td}>{l.start_date || '—'} → {l.end_date || '—'}</td>
                  <td data-label="Status" style={td}>
                    <SelectField size="small" label="" value={l.status} disabled={busy || (LOA_NEXT[l.status] ?? []).length === 0}
                      options={[l.status, ...(LOA_NEXT[l.status] ?? [])].map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
                      onChange={v => { if (v && v !== l.status) void changeLoaStatus(l.id, String(v)) }}
                      inputProps={{ 'aria-label': `LOA ${l.loa_number} status` }} sx={{ minWidth: 110 }} />
                  </td>
                  <td data-label="" style={td}>
                    <button style={linkBtn} disabled={docBusy === l.id} onClick={() => renderDoc('loa', l.id)}>
                      {docBusy === l.id ? <Loader2 className="spin" size={13} /> : <Download size={13} />} PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
          <Pager page={loasPager.page} totalPages={loasPager.totalPages} total={loasPager.total} pageSize={loasPager.pageSize} onPage={loasPager.setPage} showPageSize={false} label="LOAs" /></>
        )
      )}
      {tab === 'credentials' && (
        <div>
          {/* S6 (Andrew): nurse professional credentials — 60/30/7 expiry; an expired REQUIRED credential
              makes the nurse unassignable (the claim refuses server-side). Where TEST Nadia RN's seed lands. */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 13, color: 'var(--color-text-mute)' }}>{creds.length} credential{creds.length === 1 ? '' : 's'}</span>
            <button style={primary} onClick={() => setCredForm(v => !v)}>{credForm ? 'Cancel' : '+ Add credential'}</button>
          </div>
          {credForm && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              <input placeholder="Nurse account id" value={credB.nurse_id} onChange={e => setCredB({ ...credB, nurse_id: e.target.value })} style={input} />
              <div style={{ minWidth: 180 }}>
                <SearchableSelect value={credB.credential_type} onChange={v => setCredB({ ...credB, credential_type: v })}
                  options={['rn_license', 'malpractice', 'cpr_bls', 'tb_screen'].map(t => ({ value: t, label: t }))} placeholder="Credential type" />
              </div>
              <input placeholder="Number" value={credB.credential_number} onChange={e => setCredB({ ...credB, credential_number: e.target.value })} style={input} />
              <DateInput label="Credential expires on" value={credB.expires_at} onChange={v => setCredB({ ...credB, expires_at: v ?? '' })} style={input} />
              <button style={primary} disabled={busy || !credB.nurse_id || !credB.expires_at} onClick={async () => {
                setBusy(true); setMsg(null)
                try { await api.createCareRxCredential(id, credB); setCredForm(false); setCredB({ nurse_id: '', credential_type: 'rn_license', credential_number: '', expires_at: '' }); await load() }
                catch (e: any) { setMsg(friendlyError(e)) } finally { setBusy(false) }
              }}>Save</button>
            </div>
          )}
          {creds.length === 0 ? <Centered>No credentials recorded. The claim guard activates once mig 251 is applied and rows exist.</Centered> : (
            <><Pager page={credsPager.page} totalPages={credsPager.totalPages} total={credsPager.total} pageSize={credsPager.pageSize} onPage={credsPager.setPage} onPageSize={credsPager.setPageSize} label="credentials" />
            {credFx.chips}
            <div style={{ overflowX: 'auto' }}><table {...credFx.tableProps} style={{ width: '100%', fontSize: 13 }}>
              <thead><tr><th style={th}>Nurse {credFx.funnel('nurse')}</th><th style={th}>Type {credFx.funnel('type')}</th><th style={th}>Number {credFx.funnel('number')}</th><th style={th}>Expires {credFx.funnel('expires')}</th><th style={th}>Status {credFx.funnel('status')}</th></tr></thead>
              <tbody>
                {credsPager.pageRows.map(c => (
                  <tr key={c.id}>
                    <td style={td} title={`Account ${c.nurse_id}`}>on file</td> {/* ASP-CRX-22: GUID stays on hover only */}
                    <td style={td}>{c.credential_type}</td>
                    <td style={td}>{c.credential_number || '—'}</td>
                    <td style={td}>{formatUsDate(c.expires_at)}</td>
                    <td style={{ ...td, color: statusColor(c.expired ? 'expired' : c.days_left <= 60 ? 'expiring' : 'current') }}>
                      {credStatusText(c)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
            <Pager page={credsPager.page} totalPages={credsPager.totalPages} total={credsPager.total} pageSize={credsPager.pageSize} onPage={credsPager.setPage} showPageSize={false} label="credentials" /></>
          )}
        </div>
      )}
    </div>
  )
}

function RateCardForm({ agencyId, onSaved }: { agencyId: string; onSaved: () => void }) {
  const [f, setF] = useState({ effective_from: new Date().toISOString().slice(0, 10), first2hrs: '150', additionalPerHr: '50', mileage_per_mile: '0.65', weekend_pct: '20', holiday_pct: '50', after_hours_pct: '0', admin_fee: '0', min_visit_fee: '' })
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null)
  const set = (k: string, v: string) => setF(s => ({ ...s, [k]: v }))
  const num = (v: string) => (v === '' ? undefined : Number(v))
  const submit = async () => {
    setBusy(true); setErr(null)
    try {
      await api.createCareRxRateCard(agencyId, {
        effective_from: f.effective_from,
        per_visit: { first2hrs: num(f.first2hrs) ?? 0, additionalPerHr: num(f.additionalPerHr) ?? 0 },
        mileage_per_mile: num(f.mileage_per_mile), weekend_pct: num(f.weekend_pct), holiday_pct: num(f.holiday_pct),
        after_hours_pct: num(f.after_hours_pct), admin_fee: num(f.admin_fee), min_visit_fee: num(f.min_visit_fee),
      })
      onSaved()
    } catch (e: any) { setErr(friendlyError(e)); setBusy(false) }
  }
  const F = (label: string, k: keyof typeof f, step?: string) => (
    <L label={label}><input style={input} type="number" step={step} value={f[k]} onChange={e => set(k, e.target.value)} /></L>
  )
  return (
    <div style={panel}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        <L label="Effective from"><DateInput label="Effective from" style={input} value={f.effective_from} onChange={v => set('effective_from', v ?? '')} /></L>
        {F('First 2 hrs ($)', 'first2hrs')}
        {F('Additional /hr ($)', 'additionalPerHr')}
        {F('Mileage /mi ($)', 'mileage_per_mile', '0.01')}
        {F('Weekend %', 'weekend_pct')}
        {F('Holiday %', 'holiday_pct')}
        {F('After-hours %', 'after_hours_pct')}
        {F('Admin fee ($)', 'admin_fee')}
        {F('Min visit fee ($)', 'min_visit_fee')}
      </div>
      {err && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>{err}</div>}
      <div style={{ marginTop: 10 }}>
        <button style={primary} disabled={busy} onClick={submit}>{busy ? <Loader2 className="spin" size={14} /> : <Plus size={14} />} Save rate card</button>
      </div>
    </div>
  )
}

// A cell is a plain value, or { value, node } when it renders more than its value (ASP #9: the patient's
// language badge) — the funnels and sort always read `value`.
type RichCell = { value: unknown; node: React.ReactNode }
const isRich = (c: unknown): c is RichCell => !!c && typeof c === 'object' && 'node' in (c as object) && 'value' in (c as object)
function Table({ head, rows, empty, label = 'rows' }: { head: string[]; rows: any[][]; empty: string; icon?: React.ReactNode; label?: string }) {
  // Paginate so a large tab (e.g. visits) never renders thousands of rows into the DOM.
  // D-UI-2: one funnel per header. `head` is a fresh array on every render, so the columns are
  // keyed on its text; a cell is addressed by its position in the row.
  const headSig = head.join('\u0001')
  const columns = useMemo<FunnelColumn<any[]>[]>(
    () => headSig.split('\u0001').map((h, i) => ({ key: h, label: h, value: (r: any[]) => isRich(r[i]) ? r[i].value : r[i] })),
    [headSig])
  const fx = useTableFunnels(rows, columns)
  const { page, setPage, pageSize, setPageSize, pageRows, total, totalPages } = usePaged(fx.rows, DEFAULT_PAGE_SIZE, fx.sig)
  if (rows.length === 0) return <Centered>{empty}</Centered>
  return (
    <>
    <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} label={label} />
    {fx.chips}
    <div style={{ overflowX: 'auto' }}><table {...fx.tableProps} className="rwd-stack" style={{ width: '100%', minWidth: 'max-content', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead><tr>{head.map(h => <th key={h} style={th}>{h} {fx.funnel(h)}</th>)}</tr></thead>
      <tbody>{pageRows.map((r, i) => <tr key={i} style={{ borderBottom: '1px solid var(--color-border)' }}>{r.map((c, j) => <td key={j} data-label={head[j] || ''} style={td}>{isRich(c) ? c.node : c}</td>)}</tr>)}</tbody>
    </table></div>
    <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} showPageSize={false} label={label} />
    </>
  )
}
function Centered({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: '34px 0', textAlign: 'center', color: 'var(--color-text-mute)', fontSize: 14, display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center' }}>{children}</div>
}
function L({ label, children }: { label: string; children: React.ReactNode }) {
  return <label style={{ display: 'block', fontSize: 12 }}><span style={{ color: 'var(--color-text-mute)', display: 'block', marginBottom: 4 }}>{label}</span>{children}</label>
}

const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: .4, color: 'var(--color-text-mute)', borderBottom: '2px solid var(--color-border)' }
const td: React.CSSProperties = { padding: '9px 10px' }
const panel: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 10, padding: 16, background: 'var(--color-surface)', marginBottom: 14 }
const card: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', border: '1px solid var(--color-border)', borderRadius: 10, background: 'var(--color-surface)', cursor: 'pointer', width: '100%' }
const input: React.CSSProperties = { width: '100%', padding: '7px 9px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, background: 'var(--color-surface)', color: 'var(--color-text)', boxSizing: 'border-box' }
const primary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const ghost: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 13, cursor: 'pointer' }
const linkBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 }
const revBtn = (color: string): React.CSSProperties => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: `1px solid ${color}`, background: 'var(--color-surface)', color })
